import React, { createContext, useContext, useMemo } from "react";

/**
 * Named layer tokens that define the global stacking ladder.
 * These values are intentionally sparse so nested surfaces and portalled
 * children can derive stable offsets without needing one-off escape hatches.
 */
export const LAYER_TOKENS = {
  appBar: 1201,
  screenCover: 1202,
  rootPortal: 1300,
  drawer: 2000,
  drawerStack: 3000,
  modal: 5000,
  prioritySurface: 9000,
  storySurface: 9999,
  previewSurface: 10000,
  previewPopover: 11000,
  // Feedback must stay reachable regardless of which app surface is open.
  floatingAction: 20000,
  floatingSurface: 20200,
} as const;

/**
 * Offsets derived from a surface's content layer.
 * Overlay sits slightly below content, poppers sit slightly above it, and a
 * newly opened nested surface jumps to the next reserved block.
 */
export const LAYER_OFFSETS = {
  overlay: 20,
  portal: 40,
  nestedSurface: 200,
} as const;

export type SurfaceLayerName =
  | "drawer"
  | "modal"
  | "dialog"
  | "sheet"
  | "floatingSurface"
  | "prioritySurface"
  | "previewSurface";

const SURFACE_DEFAULT_BASES: Record<SurfaceLayerName, number> = {
  drawer: LAYER_TOKENS.drawer,
  modal: LAYER_TOKENS.modal,
  dialog: LAYER_TOKENS.modal,
  sheet: LAYER_TOKENS.modal,
  floatingSurface: LAYER_TOKENS.floatingSurface,
  prioritySurface: LAYER_TOKENS.prioritySurface,
  previewSurface: LAYER_TOKENS.previewSurface,
};

/**
 * Fully resolved layer information for a single active surface.
 */
export interface SurfaceLayerValue {
  base: number;
  overlay: number;
  portal: number;
  childSurface: number;
  tier: SurfaceLayerName;
  contentElement?: HTMLElement | null;
}

const SurfaceLayerContext = createContext<SurfaceLayerValue | null>(null);

/**
 * Builds the resolved layer map for a single surface content node.
 */
export const createSurfaceLayerValue = (
  base: number,
  tier: SurfaceLayerName,
): SurfaceLayerValue => ({
  base,
  overlay: base - LAYER_OFFSETS.overlay,
  portal: base + LAYER_OFFSETS.portal,
  childSurface: base + LAYER_OFFSETS.nestedSurface,
  tier,
});

/**
 * Resolves a surface base layer from either an explicit override, the current
 * parent surface, or the default token for the requested surface tier.
 */
export const resolveSurfaceLayerBase = ({
  tier,
  layerBase,
  parentLayer,
}: {
  tier: SurfaceLayerName;
  layerBase?: number;
  parentLayer?: SurfaceLayerValue | null;
}): number => {
  if (typeof layerBase === "number") {
    return layerBase;
  }

  if (parentLayer) {
    return parentLayer.childSurface;
  }

  return SURFACE_DEFAULT_BASES[tier];
};

/**
 * Convenience helper for the mobile drawer stack renderer.
 */
export const getDrawerStackLayerBase = (index: number): number =>
  LAYER_TOKENS.drawerStack + index * LAYER_OFFSETS.nestedSurface;

/**
 * Provides the active surface to descendants, including components rendered
 * through React portals.
 */
export const SurfaceLayerProvider: React.FC<{
  value: SurfaceLayerValue;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <SurfaceLayerContext.Provider value={value}>
    {children}
  </SurfaceLayerContext.Provider>
);

/**
 * Returns the currently active surface layer, if any.
 */
export const useActiveSurfaceLayer = (): SurfaceLayerValue | null =>
  useContext(SurfaceLayerContext);

/**
 * Resolves the full layer map for a blocking surface primitive.
 */
export const useResolvedSurfaceLayer = ({
  tier,
  layerBase,
}: {
  tier: SurfaceLayerName;
  layerBase?: number;
}): SurfaceLayerValue => {
  const parentLayer = useActiveSurfaceLayer();

  return useMemo(() => {
    const resolvedBase = resolveSurfaceLayerBase({
      tier,
      layerBase,
      parentLayer,
    });

    return createSurfaceLayerValue(resolvedBase, tier);
  }, [layerBase, parentLayer, tier]);
};

/**
 * Resolves the z-index for a portalled child surface such as Select, Popover,
 * DropdownMenu, Tooltip, or HoverCard content.
 */
export const usePortalLayer = (layerBase?: number): number => {
  const activeLayer = useActiveSurfaceLayer();

  return useMemo(() => {
    if (typeof layerBase === "number") {
      return layerBase;
    }

    return activeLayer?.portal ?? LAYER_TOKENS.rootPortal;
  }, [activeLayer, layerBase]);
};
